<template>
  <div class="navigation-container">
    <!-- Mobile Overlay -->
    <div 
      v-if="isMobile && drawer" 
      class="mobile-overlay"
      @click="drawer = false"
    ></div>
    
    <!-- Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      :permanent="!isMobile"
      :temporary="isMobile"
      :width="isCollapsed ? 72 : 280"
      class="modern-nav-drawer"
      :class="{ 'collapsed': isCollapsed }"
    >
      <!-- Header Section -->
      <div class="nav-header">
        <div class="brand-section" :class="{ 'collapsed': isCollapsed }">
          <v-avatar 
            size="40" 
            class="brand-avatar"
            :class="{ 'mini': isCollapsed }"
          >
            <v-icon color="white" size="24" @click="isCollapsed = !isCollapsed">mdi-menu-open</v-icon>
          </v-avatar>
          <transition name="fade-slide">
            <div v-if="!isCollapsed" class="brand-text">
              <h3 class="brand-title">Admin Panel</h3> 
              <p class="brand-subtitle">Management Hub</p>
            </div>
          </transition>
        </div>
        
        <!-- Collapse Toggle (Desktop only) -->
        <!-- <v-btn 
          v-if="!isMobile"
          icon
          small
          class="collapse-btn"
          @click="isCollapsed = !isCollapsed"
        >
          <v-icon size="20" color="rgba(255,255,255,0.7)">
            {{ isCollapsed ? 'mdi-menu' : 'mdi-menu-open' }}
          </v-icon>
        </v-btn> -->
      </div>

      <!-- Navigation Items -->
      <div class="nav-content">
        <v-list class="nav-list">
          <div 
            v-for="(item, i) in menuItems" 
            :key="i"
            class="nav-item-wrapper"
          >
            <v-list-item
              :class="[
                'nav-item',
                { 
                  'active': selectedItem === i,
                  'collapsed': isCollapsed
                }
              ]"
              @click="onOpenMenu(item.name, i)"
              :ref="`menuItem-${i}`"
            >
              <div class="nav-item-content">
                <div class="nav-icon-wrapper">
                  <v-icon 
                    :color="selectedItem === i ? '#FFD700' : 'rgba(255,255,255,0.8)'"
                    size="22"
                  >
                    {{ item.icon }}
                  </v-icon>
                </div>
                
                <transition name="fade-slide">
                  <div v-if="!isCollapsed" class="nav-text">
                    <span class="nav-title">{{ item.text }}</span>
                    <div v-if="item.badge" class="nav-badge">{{ item.badge }}</div>
                  </div>
                </transition>
              </div>
              
              <!-- Active indicator -->
              <div v-if="selectedItem === i" class="active-indicator"></div>
              
              <!-- Hover effect -->
              <div class="hover-effect"></div>
            </v-list-item>
            
            <!-- Tooltip for collapsed state -->
            <v-tooltip 
              v-if="isCollapsed" 
              right
              :open-delay="200"
            >
              <template v-slot:activator="{ on, attrs }">
                <div 
                  class="tooltip-trigger"
                  v-bind="attrs"
                  v-on="on"
                ></div>
              </template>
              <span>{{ item.text }}</span>
            </v-tooltip>
          </div>
        </v-list>
      </div>

      <!-- Footer Section -->
      <div class="nav-footer">
        <div class="user-section" :class="{ 'collapsed': isCollapsed }">
          <v-avatar size="36" class="user-avatar">
            <v-icon color="white">mdi-account</v-icon>
          </v-avatar>
          <transition name="fade-slide">
            <div v-if="!isCollapsed" class="user-info">
              <p class="user-name">Admin User</p>
              <p class="user-role">Administrator</p>
            </div>
          </transition>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- Mobile Menu Toggle -->
    <v-btn
      v-if="isMobile"
      fab
      small
      fixed
      bottom
      right
      color="primary"
      class="mobile-menu-toggle"
      @click="drawer = !drawer"
    >
      <v-icon>{{ drawer ? 'mdi-close' : 'mdi-menu' }}</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "ModernNavigationDrawer",
  data() {
    return {
      drawer: true,
      isCollapsed: false,
      isMobile: false,
      selectedItem: -1,
      menuItems: [
        { icon: "mdi-view-dashboard", text: "Dashboard", name: "admin-home" },
        { icon: "mdi mdi-security", text: "Admin", name: "admin-manage" },
        { icon: "mdi-account-group", text: "Volunteers", name: "admin-volunteers" },
        { icon: "mdi-domain", text: "Agencies", name: "admin-agencies" },
        { icon: "mdi-briefcase-variant", text: "Businesses", name: "admin-company" },
        { icon: "mdi-marker-check", text: "Needs", name: "agency-need-feeds" },
        { icon: "mdi-calendar-text", text: "Events", name: "events" },
        { icon: "mdi-message-fast", text: "Send Message", name: "admin-message" },
        { icon: "mdi-map", text: "Map", name: "need-map" },
        { icon: "mdi-advertisements", text: "Advertisements", name: "admin-ads" },
        { icon: "mdi-post", text: "Blogs", name: "blog-feeds" },
        // { icon: "mdi-cog", text: "Settings", name: "settings" },
        { icon: "mdi-account", text: "My Profile", name: "admin-profile" },
        { icon: "mdi-currency-usd", text: "Donations", name: "admin-donations" },
        { icon: "mdi-cash-check", text: "Commissions", name: "admin-commissions" },
        { icon: "mdi-invert-colors", text: "Colors", name: "colors" },
        { icon: "mdi-logout", text: "Log Out", name: "logout" }
      ]
    };
  },
  methods: {
    ...mapActions({
      onLogout: "auth/onLogout"
    }),
    
    onOpenMenu(name, index) {
      this.selectedItem = index;
      
      if (name === "logout") {
        this.onLogout().then(() => {
          this.$router.push({ name: "login" });
        });
      } else if (this.$route.name !== name) {
        this.$router.push({ name });
      }
      
      // Close drawer on mobile after selection
      if (this.isMobile) {
        this.drawer = false;
      }
    },
    
    checkMobile() {
      this.isMobile = window.innerWidth < 960;
      if (this.isMobile) {
        this.drawer = false;
        this.isCollapsed = false;
      }
    },
    
    handleResize() {
      this.checkMobile();
    }
  },
  
  mounted() {
    // Set initial mobile state
    this.checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', this.handleResize);
    
    // Find current route and set active item
    for (let i = 0; i < this.menuItems.length; i++) {
      if (this.menuItems[i].name === this.$route.name) {
        this.selectedItem = i;
        break;
      }
    }
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
/* Base Navigation Styles */
.navigation-container {
  position: relative;
}

.modern-nav-drawer {
  background: linear-gradient(180deg, #0a4d6b 0%, #084468 100%) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: sticky !important;
  top: 60px !important;
  left: 200;
  height: 100vh !important;
  z-index: 190;
  overflow: hidden !important;
}

.modern-nav-drawer.collapsed {
  overflow: visible !important;
}

/* Mobile Overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  backdrop-filter: blur(4px);
}

/* Header Section */
.nav-header {
  padding: 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.brand-section.collapsed {
  justify-content: center;
}

.brand-avatar {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  box-shadow: 0 4px 16px rgba(255, 215, 0, 0.3);
  transition: all 0.3s ease;
}

.brand-avatar.mini {
  transform: scale(0.9);
}

.brand-text {
  color: white;
}

.brand-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.brand-subtitle {
  font-size: 12px;
  opacity: 0.7;
  margin: 0;
  line-height: 1;
}

.collapse-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
}

.collapse-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.05);
}

/* Navigation Content */
.nav-content {
  flex: 1;
  padding: 16px 0;
  overflow: visible !important; /* Remove scrolling from nav-content */
}

.nav-content::-webkit-scrollbar {
  width: 4px;
}

.nav-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.nav-content::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.5);
  border-radius: 2px;
}

.nav-list {
  padding: 0;
  background: transparent !important;
}

.nav-item-wrapper {
  position: relative;
  margin: 2px 8px;
}

.nav-item {
  position: relative;
  border-radius: 12px !important;
  margin: 0 !important;
  padding: 0 !important;
  min-height: 48px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;
}

.nav-item.collapsed {
  justify-content: center;
  border-radius: 50% !important;
  width: 48px;
  height: 48px;
  margin: 8px auto !important;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateX(4px);
}

.nav-item.active {
  background: rgba(255, 215, 0, 0.15) !important;
  border-left: 3px solid #FFD700;
  transform: translateX(4px);
}

.nav-item-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  width: 100%;
  gap: 16px;
}

.nav-item.collapsed .nav-item-content {
  justify-content: center;
  padding: 12px;
}

.nav-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  transition: all 0.3s ease;
}

.nav-item.active .nav-icon-wrapper {
  transform: scale(1.1);
}

.nav-text {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-title {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.nav-item.active .nav-title {
  color: #FFD700;
  font-weight: 600;
}

.nav-badge {
  background: #FF6B6B;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  animation: pulse 2s infinite;
}

.active-indicator {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #FFD700 0%, #FFA500 100%);
  border-radius: 2px 0 0 2px;
  box-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
}

.hover-effect {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.nav-item:hover .hover-effect {
  left: 100%;
}

.tooltip-trigger {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

/* Footer Section */
.nav-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
}

.user-section.collapsed {
  justify-content: center;
}

.user-avatar {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.user-info {
  color: white;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.user-role {
  font-size: 12px;
  opacity: 0.7;
  margin: 0;
  line-height: 1;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  z-index: 1000;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
}

/* Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive Design */
@media (max-width: 959px) {
  .nav-header {
    padding: 16px;
  }
  
  .brand-title {
    font-size: 16px;
  }
  
  .nav-item-content {
    padding: 10px 16px;
  }
  
  .nav-title {
    font-size: 13px;
  }
}

@media (max-width: 599px) {
  .modern-nav-drawer {
    width: 280px !important;
  }
  
  .nav-header {
    padding: 12px 16px;
  }
  
  .brand-title {
    font-size: 15px;
  }
  
  .nav-item-content {
    padding: 8px 16px;
  }
}

/* Custom scrollbar for Webkit browsers */
.modern-nav-drawer::-webkit-scrollbar {
  width: 6px;
}

.modern-nav-drawer::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.modern-nav-drawer::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.5);
  border-radius: 3px;
}

.modern-nav-drawer::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.7);
}
</style>